import { getReplay } from "@dailywars/core/db";
import { mapIdToVariant, type GameState, type Move } from "@dailywars/core/engine";
import type { MapVariant } from "@dailywars/core/engine/maps/mapId";
import { toUtcDateString } from "@dailywars/core/entitlements/mapAccess";
import { createServerFn } from "@tanstack/react-start";
import * as z from "zod";

import { GUEST_DISPLAY_NAME } from "#/lib/playerDisplayName";
import { setRouteAction } from "#/lib/requestLogger.server";
import { replayIdSchema } from "#/lib/routeParams";
import { resolveLeaderboardDisplayNames } from "#/server/game/leaderboardPlayers";
import { resolveRequestGameContext } from "#/server/game/requestGameContext.server";

const loadReplayInputSchema = z.object({
  replayId: replayIdSchema,
});

export type ReplayPageData = {
  locked: boolean;
  initialState: GameState;
  moves: Move[];
  mapId: string;
  mapDate: string;
  variant: MapVariant;
  playerId: string;
  displayName: string;
  finalServerDayNumber: number;
};

export function isReplayLocked({
  mapDate,
  replayPlayerId,
  viewerPlayerId,
  today = toUtcDateString(new Date()),
}: {
  mapDate: string;
  replayPlayerId: string;
  viewerPlayerId: string;
  today?: string;
}): boolean {
  return mapDate === today && viewerPlayerId !== replayPlayerId;
}

async function replayPageData({
  playerId,
  viewerPlayerId,
  mapId,
  mapDate,
  initialState,
  moves,
  finalServerDayNumber,
}: {
  playerId: string;
  viewerPlayerId: string;
  mapId: string;
  mapDate: string;
  initialState: GameState;
  moves: Move[];
  finalServerDayNumber: number;
}): Promise<ReplayPageData> {
  const names = await resolveLeaderboardDisplayNames([playerId]);
  const displayName = names.get(playerId) ?? GUEST_DISPLAY_NAME;
  const variant = mapIdToVariant(mapId);
  const locked = isReplayLocked({
    mapDate,
    replayPlayerId: playerId,
    viewerPlayerId,
  });

  if (locked) {
    return {
      locked: true,
      initialState,
      moves: [],
      mapId,
      mapDate,
      variant,
      playerId,
      displayName,
      finalServerDayNumber: 1,
    };
  }

  return {
    locked: false,
    initialState,
    moves,
    mapId,
    mapDate,
    variant,
    playerId,
    displayName,
    finalServerDayNumber,
  };
}

export const loadReplay = createServerFn({ method: "GET" })
  .validator(loadReplayInputSchema)
  .handler(async ({ data }): Promise<ReplayPageData | null> => {
    setRouteAction("replay_page");
    const loaded = await getReplay(data.replayId);
    if (!loaded) return null;
    const { playerId: viewerPlayerId } = await resolveRequestGameContext();

    return replayPageData({
      playerId: loaded.playerId,
      viewerPlayerId,
      mapId: loaded.mapId,
      mapDate: loaded.mapDate,
      initialState: loaded.initialState,
      moves: loaded.moves,
      finalServerDayNumber: loaded.finalServerDayNumber,
    });
  });
